import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import UserCreditHistoryPanel from "./UserCreditHistoryPanel";
import UserSendHistoryPanel from "./UserSendHistoryPanel";
import CreditAdjustModal from "./CreditAdjustModal";

interface User {
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  plan: string;
  status: string;
  credit: number;
  created_at: string;
  total_sent: number;
  total_charged: number;
  per_message_rate: number | null;
  is_agency: boolean;
  parent_agency_id: string | null;
  referral_code: string | null;
}

interface Agency {
  id: string;
  full_name: string | null;
  email: string | null;
}

interface Props {
  user: User;
  onClose: () => void;
  onUpdated: () => void;
}

const PLAN_COLORS: Record<string, string> = {
  FREE: "bg-gray-500/20 text-gray-400",
  BASIC: "bg-sky-500/20 text-sky-400",
  PRO: "bg-violet-500/20 text-violet-400",
  ENTERPRISE: "bg-rose-500/20 text-rose-400",
};

const STATUS_COLORS: Record<string, string> = {
  active: "bg-emerald-500/20 text-emerald-400",
  inactive: "bg-gray-500/20 text-gray-400",
  banned: "bg-red-500/20 text-red-400",
  pending: "bg-amber-500/20 text-amber-400",
};

const STATUS_LABELS: Record<string, string> = {
  active: "활성",
  inactive: "비활성",
  banned: "정지",
  pending: "승인 대기",
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
}

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

export default function UserDetailModal({ user, onClose, onUpdated }: Props) {
  const { session } = useAuth();
  const [activeTab, setActiveTab] = useState<"info" | "credit" | "send">("info");
  const [rateValue, setRateValue] = useState<string>(
    user.per_message_rate != null ? String(user.per_message_rate) : ""
  );
  const [savingRate, setSavingRate] = useState(false);
  const [rateSaved, setRateSaved] = useState(false);
  const [rateError, setRateError] = useState<string | null>(null);

  // Status change
  const [currentStatus, setCurrentStatus] = useState(user.status || "active");
  const [changingStatus, setChangingStatus] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusSaved, setStatusSaved] = useState(false);

  // Agency
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [currentAgencyId, setCurrentAgencyId] = useState<string | null>(user.parent_agency_id);
  const [savingAgency, setSavingAgency] = useState(false);
  const [agencySaved, setAgencySaved] = useState(false);
  const [agencyError, setAgencyError] = useState<string | null>(null);

  // Referral code
  const [referralCodeValue, setReferralCodeValue] = useState<string>(user.referral_code || "");
  const [savingReferralCode, setSavingReferralCode] = useState(false);
  const [referralSaved, setReferralSaved] = useState(false);
  const [referralError, setReferralError] = useState<string | null>(null);
  const [referralCopied, setReferralCopied] = useState(false);

  // Credit adjust
  const [showCreditAdjust, setShowCreditAdjust] = useState(false);
  const [currentCredit, setCurrentCredit] = useState(user.credit || 0);

  // Reset when user changes
  useEffect(() => {
    setRateValue(user.per_message_rate != null ? String(user.per_message_rate) : "");
    setRateSaved(false);
    setRateError(null);
    setCurrentStatus(user.status || "active");
    setStatusError(null);
    setStatusSaved(false);
    setCurrentAgencyId(user.parent_agency_id);
    setAgencySaved(false);
    setAgencyError(null);
    setReferralCodeValue(user.referral_code || "");
    setReferralSaved(false);
    setReferralError(null);
    setCurrentCredit(user.credit || 0);
    setShowCreditAdjust(false);
  }, [user.id]);

  // Load agencies
  useEffect(() => {
    const fetchAgencies = async () => {
      const token = session?.access_token;
      if (!token) return;
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/get-admin-agencies`, {
          headers: {
            Authorization: `Bearer ${token}`,
            apikey: SUPABASE_ANON_KEY,
            "Content-Type": "application/json",
          },
        });
        const json = await res.json();
        if (json.error) throw new Error(json.error);
        setAgencies(json.agencies ?? []);
      } catch (err: any) {
        console.error("[UserDetail] fetch agencies error:", err);
      }
    };
    fetchAgencies();
  }, [session]);

  // Close on ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const handleSaveRate = useCallback(async () => {
    setRateError(null);
    setRateSaved(false);
    const token = session?.access_token;
    if (!token) {
      setRateError("로그인이 필요합니다.");
      return;
    }

    let payloadRate: number | null = null;
    if (rateValue !== "" && rateValue !== null && rateValue !== undefined) {
      const numeric = Number(rateValue);
      if (Number.isNaN(numeric) || numeric < 0) {
        setRateError("유효한 금액을 입력하세요.");
        return;
      }
      payloadRate = numeric;
    }

    setSavingRate(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/update-user-rate`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: SUPABASE_ANON_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: user.id,
          per_message_rate: payloadRate,
        }),
      });
      const resBody = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(resBody?.error || `HTTP ${res.status}`);
      }
      setRateSaved(true);
      setRateValue(payloadRate !== null ? String(payloadRate) : "");
      onUpdated();
      setTimeout(() => setRateSaved(false), 2000);
    } catch (err: any) {
      setRateError(err.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setSavingRate(false);
    }
  }, [session, rateValue, user.id, onUpdated]);

  const handleChangeStatus = useCallback(async (newStatus: string) => {
    setStatusError(null);
    setStatusSaved(false);
    const token = session?.access_token;
    if (!token) {
      setStatusError("로그인이 필요합니다.");
      return;
    }
    setChangingStatus(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/update-user-status`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: SUPABASE_ANON_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: user.id,
          status: newStatus,
        }),
      });
      const resBody = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(resBody?.error || `HTTP ${res.status}`);
      }
      setCurrentStatus(newStatus);
      setStatusSaved(true);
      onUpdated();
      setTimeout(() => setStatusSaved(false), 2000);
    } catch (err: any) {
      setStatusError(err.message || "상태 변경 중 오류가 발생했습니다.");
    } finally {
      setChangingStatus(false);
    }
  }, [session, user.id, onUpdated]);

  const handleSaveAgency = useCallback(async () => {
    setAgencyError(null);
    setAgencySaved(false);
    const token = session?.access_token;
    if (!token) {
      setAgencyError("로그인이 필요합니다.");
      return;
    }
    setSavingAgency(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/update-agency-assignment`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: SUPABASE_ANON_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: user.id,
          parent_agency_id: currentAgencyId,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.error || `HTTP ${res.status}`);
      }
      setAgencySaved(true);
      onUpdated();
      setTimeout(() => setAgencySaved(false), 2000);
    } catch (err: any) {
      setAgencyError(err.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setSavingAgency(false);
    }
  }, [session, currentAgencyId, user.id, onUpdated]);

  const handleSaveReferralCode = useCallback(async () => {
    setReferralError(null);
    setReferralSaved(false);
    const token = session?.access_token;
    if (!token) {
      setReferralError("로그인이 필요합니다.");
      return;
    }
    setSavingReferralCode(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/update-referral-code`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: SUPABASE_ANON_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: user.id,
          referral_code: referralCodeValue.trim() || null,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.error || `HTTP ${res.status}`);
      }
      setReferralSaved(true);
      onUpdated();
      setTimeout(() => setReferralSaved(false), 2000);
    } catch (err: any) {
      setReferralError(err.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setSavingReferralCode(false);
    }
  }, [session, referralCodeValue, user.id, onUpdated]);

  const handleCopyReferralCode = useCallback(() => {
    if (!referralCodeValue) return;
    navigator.clipboard.writeText(referralCodeValue).then(() => {
      setReferralCopied(true);
      setTimeout(() => setReferralCopied(false), 2000);
    });
  }, [referralCodeValue]);

  const handleCreditAdjustSuccess = useCallback((newCredit: number) => {
    setCurrentCredit(newCredit);
    onUpdated();
  }, [onUpdated]);

  const displayName = user.full_name || user.email || "이름 없음";
  const planLabel = user.plan || "FREE";
  const currentAgencyName = currentAgencyId
    ? (agencies.find((a) => a.id === currentAgencyId)?.full_name || agencies.find((a) => a.id === currentAgencyId)?.email || "알 수 없음")
    : null;

  const statusOptions = [
    { value: "active", label: "활성", desc: "정상 사용 가능", icon: "ri-check-line", color: "text-emerald-400", border: "border-emerald-500/30", bg: "bg-emerald-500/10" },
    { value: "inactive", label: "비활성", desc: "로그인 가능, 발송 불가", icon: "ri-pause-line", color: "text-gray-400", border: "border-white/[0.1]", bg: "bg-white/[0.04]" },
    { value: "banned", label: "정지", desc: "모든 기능 차단", icon: "ri-forbid-line", color: "text-red-400", border: "border-red-500/30", bg: "bg-red-500/10" },
    { value: "pending", label: "승인 대기", desc: "관리자 승인 필요", icon: "ri-time-line", color: "text-amber-400", border: "border-amber-500/30", bg: "bg-amber-500/10" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>

      {/* Modal */}
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0c0c1a] shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08] shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-orange-500 text-white text-sm font-semibold shrink-0">
              {displayName.charAt(0).toUpperCase()}
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">{displayName}</h3>
              <p className="text-gray-500 text-xs">{user.email || "-"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Credit Adjust button in header */}
            <button
              onClick={() => setShowCreditAdjust(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-coins-line text-sm"></i>
              크레딧 조정
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-6 pt-3 border-b border-white/[0.06] shrink-0">
          {[
            { key: "info" as const, label: "기본 정보", icon: "ri-user-line" },
            { key: "credit" as const, label: "크레딧 내역", icon: "ri-coin-line" },
            { key: "send" as const, label: "발송 내역", icon: "ri-send-plane-line" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold cursor-pointer whitespace-nowrap border-b-2 transition-all ${
                activeTab === t.key
                  ? "text-rose-400 border-rose-500"
                  : "text-gray-500 border-transparent hover:text-gray-300"
              }`}
            >
              <i className={`${t.icon} text-sm`}></i>
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === "info" && (
            <div className="space-y-5">
              {/* Referral Code Section (only for agency members) */}
              {user.is_agency && (
                <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-500/10">
                      <i className="ri-gift-line text-amber-400 text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-white text-sm font-semibold">추천인 코드</h4>
                      <p className="text-gray-500 text-xs">에이전시 추천인 코드를 설정하거나 수정합니다</p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
                    <div className="flex-1 w-full">
                      <div className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 focus-within:border-amber-500/40 transition-colors">
                        <span className="text-gray-500 text-sm font-medium">
                          <i className="ri-gift-line"></i>
                        </span>
                        <input
                          type="text"
                          value={referralCodeValue}
                          onChange={(e) => {
                            setReferralCodeValue(e.target.value.toUpperCase());
                            setReferralSaved(false);
                            setReferralError(null);
                          }}
                          placeholder="미설정"
                          className="flex-1 bg-transparent text-white text-sm font-medium placeholder-gray-600 focus:outline-none uppercase tracking-widest"
                        />
                        {referralCodeValue && (
                          <button
                            type="button"
                            onClick={handleCopyReferralCode}
                            className="text-gray-500 hover:text-amber-400 cursor-pointer transition-colors"
                          >
                            <i className={`${referralCopied ? "ri-checkbox-circle-line text-emerald-400" : "ri-file-copy-line"} text-sm`}></i>
                          </button>
                        )}
                      </div>
                      {referralError && (
                        <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                          <i className="ri-error-warning-line"></i>
                          {referralError}
                        </p>
                      )}
                      {referralSaved && (
                        <p className="text-emerald-400 text-xs mt-1.5 flex items-center gap-1">
                          <i className="ri-checkbox-circle-line"></i>
                          저장 완료
                        </p>
                      )}
                    </div>
                    <button
                      onClick={handleSaveReferralCode}
                      disabled={savingReferralCode}
                      className="px-5 py-2.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-colors"
                    >
                      {savingReferralCode ? (
                        <span className="flex items-center gap-1.5">
                          <i className="ri-loader-4-line animate-spin"></i>
                          저장 중
                        </span>
                      ) : (
                        "코드 저장"
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Status Change Section */}
              <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-rose-500/10">
                    <i className="ri-shield-user-line text-rose-400 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="text-white text-sm font-semibold">회원 상태</h4>
                    <p className="text-gray-500 text-xs">현재 상태를 변경하여 회원의 서비스 이용을 제어합니다</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {statusOptions.map((opt) => {
                    const isActive = currentStatus === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => handleChangeStatus(opt.value)}
                        disabled={changingStatus || isActive}
                        className={`flex items-center gap-2.5 px-3 py-3 rounded-xl border text-left cursor-pointer transition-all ${
                          isActive
                            ? `${opt.bg} ${opt.border}`
                            : "bg-white/[0.02] border-white/[0.06] hover:border-white/[0.15]"
                        }`}
                      >
                        <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${isActive ? opt.bg : "bg-white/[0.04]"}`}>
                          <i className={`${opt.icon} ${isActive ? opt.color : "text-gray-600"}`}></i>
                        </div>
                        <div>
                          <p className={`text-sm font-semibold ${isActive ? opt.color : "text-white"}`}>{opt.label}</p>
                          <p className="text-gray-600 text-[11px] mt-0.5">{opt.desc}</p>
                        </div>
                        {isActive && (
                          <i className="ri-check-line text-emerald-400 text-sm ml-auto"></i>
                        )}
                      </button>
                    );
                  })}
                </div>

                {statusError && (
                  <p className="text-red-400 text-xs mt-2.5 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    {statusError}
                  </p>
                )}
                {statusSaved && (
                  <p className="text-emerald-400 text-xs mt-2.5 flex items-center gap-1">
                    <i className="ri-checkbox-circle-line"></i>
                    상태가 변경되었습니다
                  </p>
                )}
              </div>

              {/* Basic Info Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {[
                  { label: "이름", value: user.full_name || "-" },
                  { label: "이메일", value: user.email || "-" },
                  { label: "전화번호", value: user.phone || "-" },
                  { label: "플랜", value: (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PLAN_COLORS[planLabel] ?? PLAN_COLORS.FREE}`}>
                      {planLabel}
                    </span>
                  ) },
                  { label: "회원 유형", value: (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${user.is_agency ? "bg-amber-500/10 text-amber-400" : "bg-gray-500/10 text-gray-500"}`}>
                      {user.is_agency ? "에이전시" : "일반 회원"}
                    </span>
                  ) },
                  { label: "상태", value: (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[currentStatus] ?? STATUS_COLORS.active}`}>
                      {STATUS_LABELS[currentStatus] || currentStatus}
                    </span>
                  ) },
                  { label: "소속 에이전시", value: (
                    currentAgencyName ? (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-500/10 text-amber-400 whitespace-nowrap">
                        <i className="ri-building-4-line mr-1 text-[10px]"></i>
                        {currentAgencyName}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500">미배정</span>
                    )
                  ) },
                  { label: "추천인 코드", value: (
                    user.referral_code ? (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-500/10 text-amber-400 whitespace-nowrap tracking-widest">
                        <i className="ri-gift-line mr-1 text-[10px]"></i>
                        {user.referral_code}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500">미설정</span>
                    )
                  ) },
                  { label: "가입일", value: formatDate(user.created_at) },
                  { label: "총 발송", value: `${(user.total_sent || 0).toLocaleString()}건` },
                  { label: "총 충전", value: `₩${(user.total_charged || 0).toLocaleString()}` },
                  { label: "현재 크레딧", value: (
                    <span className="text-emerald-400 font-bold">₩{(currentCredit || 0).toLocaleString()}</span>
                  ) },
                  { label: "회원 ID", value: (
                    <span className="text-gray-500 text-xs font-mono truncate block">{user.id}</span>
                  ) },
                ].map((item) => (
                  <div key={item.label} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3">
                    <p className="text-gray-600 text-[11px] uppercase tracking-wider font-semibold mb-1">{item.label}</p>
                    <div className="text-white text-sm font-medium">{item.value}</div>
                  </div>
                ))}
              </div>

              {/* Agency Assignment Section */}
              {!user.is_agency && (
                <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-500/10">
                      <i className="ri-building-4-line text-amber-400 text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-white text-sm font-semibold">에이전시 소속</h4>
                      <p className="text-gray-500 text-xs">이 회원의 소속 에이전시를 설정하거나 해제합니다</p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
                    <div className="flex-1 w-full">
                      <select
                        value={currentAgencyId ?? ""}
                        onChange={(e) => {
                          setCurrentAgencyId(e.target.value || null);
                          setAgencySaved(false);
                          setAgencyError(null);
                        }}
                        className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-amber-500/40 cursor-pointer"
                      >
                        <option value="" className="bg-[#0c0c1a] text-gray-400">소속 해제 (미배정)</option>
                        {agencies.map((agency) => (
                          <option key={agency.id} value={agency.id} className="bg-[#0c0c1a] text-white">
                            {agency.full_name || agency.email || "이름 없음"}
                          </option>
                        ))}
                      </select>
                      {agencyError && (
                        <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                          <i className="ri-error-warning-line"></i>
                          {agencyError}
                        </p>
                      )}
                      {agencySaved && (
                        <p className="text-emerald-400 text-xs mt-1.5 flex items-center gap-1">
                          <i className="ri-checkbox-circle-line"></i>
                          저장 완료
                        </p>
                      )}
                    </div>
                    <button
                      onClick={handleSaveAgency}
                      disabled={savingAgency}
                      className="px-5 py-2.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-colors"
                    >
                      {savingAgency ? (
                        <span className="flex items-center gap-1.5">
                          <i className="ri-loader-4-line animate-spin"></i>
                          저장 중
                        </span>
                      ) : (
                        "저장"
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Per Message Rate Setting */}
              <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-500/10">
                    <i className="ri-coins-line text-amber-400 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="text-white text-sm font-semibold">건당 발송 가격</h4>
                    <p className="text-gray-500 text-xs">이 회원의 메시지 1건당 차감되는 크레딧 금액을 설정합니다</p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
                  <div className="flex-1 w-full">
                    <div className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 focus-within:border-rose-500/40 transition-colors">
                      <span className="text-gray-500 text-sm font-medium">₩</span>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={rateValue}
                        onChange={(e) => {
                          setRateValue(e.target.value);
                          setRateSaved(false);
                          setRateError(null);
                        }}
                        placeholder="미설정 (기본값 사용)"
                        className="flex-1 bg-transparent text-white text-sm font-medium placeholder-gray-600 focus:outline-none"
                      />
                    </div>
                    {rateError && (
                      <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>
                        {rateError}
                      </p>
                    )}
                    {rateSaved && (
                      <p className="text-emerald-400 text-xs mt-1.5 flex items-center gap-1">
                        <i className="ri-checkbox-circle-line"></i>
                        저장 완료
                      </p>
                    )}
                  </div>
                  <button
                    onClick={handleSaveRate}
                    disabled={savingRate}
                    className="px-5 py-2.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-colors"
                  >
                    {savingRate ? (
                      <span className="flex items-center gap-1.5">
                        <i className="ri-loader-4-line animate-spin"></i>
                        저장 중
                      </span>
                    ) : (
                      "가격 저장"
                    )}
                  </button>
                </div>

                <div className="mt-3 flex items-center gap-1.5 text-gray-600 text-xs">
                  <i className="ri-information-line"></i>
                  <span>비워두면 기본 요금표가 적용됩니다. 숫자를 입력하면 해당 회원에게만 적용됩니다.</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === "credit" && (
            <UserCreditHistoryPanel userId={user.id} />
          )}

          {activeTab === "send" && (
            <UserSendHistoryPanel userId={user.id} userName={displayName} totalSent={user.total_sent || 0} />
          )}
        </div>
      </div>

      {/* Credit Adjust Modal */}
      {showCreditAdjust && (
        <CreditAdjustModal
          user={{
            id: user.id,
            full_name: user.full_name,
            email: user.email,
            credit: currentCredit,
          }}
          onClose={() => setShowCreditAdjust(false)}
          onSuccess={handleCreditAdjustSuccess}
        />
      )}
    </div>
  );
}